import { ReactNode } from 'react';
import {
  useSidebarPinState,
  SidebarGroup as CoreSidebarGroup,
  SidebarGroupProps,
} from '@backstage/core-components';
import { CollapsedSidebarGroup } from './CollapsedSidebarGroup';
import { ExpandedSidebarGroup } from './ExpandedSidebarGroup';
import { useExpandableSidebarGroupState } from './useExpandableSidebarGroupState';

export type ExpandableSidebarGroupProps = Omit<SidebarGroupProps, 'to'> & {
  children: ReactNode;
  id?: string;
};

/**
 * Expandable sidebar group component that replaces fly-out submenus with inline dropdowns
 * When collapsed, shows a hover popout submenu instead of expanding inline
 */
export const ExpandableSidebarGroup = (props: ExpandableSidebarGroupProps) => {
  const { children, icon, label, id } = props;
  const { isMobile } = useSidebarPinState();

  // Hook handles all shared state
  const state = useExpandableSidebarGroupState({ children, id, label });

  // Fallback to regular SidebarGroup behavior on mobile
  if (isMobile) {
    return <CoreSidebarGroup {...props} />;
  }

  if (state.isCollapsed) {
    return (
      <CollapsedSidebarGroup
        icon={icon}
        label={label}
        groupId={state.groupId}
        hasActiveChild={state.hasActiveChild}
        isHovered={state.isHovered}
        isMainItemHovered={state.isMainItemHovered}
        isSubmenuActive={state.isSubmenuActive}
        onGroupHover={state.onGroupHover}
        handleMainItemMouseEnter={state.handleMainItemMouseEnter}
        handleMainItemMouseLeave={state.handleMainItemMouseLeave}
        handlePopoutMouseEnter={state.handlePopoutMouseEnter}
        handlePopoutMouseLeave={state.handlePopoutMouseLeave}
        handleMainItemFocus={state.handleMainItemFocus}
        handleMainItemBlur={state.handleMainItemBlur}
        handleActivateSubmenu={state.handleActivateSubmenu}
        handleDeactivateSubmenu={state.handleDeactivateSubmenu}
        onGroupFocus={state.onGroupFocus}
        mainItemRef={state.mainItemRef}
        popoutRef={state.popoutRef}
      >
        {children}
      </CollapsedSidebarGroup>
    );
  }

  return (
    <ExpandedSidebarGroup
      icon={icon}
      label={label}
      isExpanded={state.isExpanded}
      hasActiveChild={state.hasActiveChild}
      onToggle={state.handleToggle}
    >
      {children}
    </ExpandedSidebarGroup>
  );
};
